import { PropertyValueMap, TemplateResult, html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksSegmentItemSelect } from '../../events/blocks-segment-item-select';
import { BlocksElement } from '../BlocksElement';
import '../skeleton/skeleton';
import styles from './segmented-control-item.styles';
import { SlotController } from '../../controllers/slot-controller';

/**
 * @slot default - The component content
 * @slot before - use this slot to show content before default slot/content
 * @slot after - use this slot to show content after default slot/content
 * @fires bl-segment-item-select - emitted when a segmented-item is clicked.
 * @csspart base - The component's base wrapper.
 * @csspart button - The component's `<button>`.
 * @csspart part-before - The component's before slot.
 * @csspart wrapper - The component's wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart part-after - The component's after slot.
 * @csspart skeleton - The component's skeleton element.
 */
export class BlocksSegmentedControlItem extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Set Active property
   */
  @property({ type: Boolean, reflect: true })
  isActive: boolean | undefined;

  /**
   * Disabled attribute
   */
  @property({ type: Boolean, reflect: true })
  disabled: boolean | undefined;

  /**
   * set to true when only icon is to be kept as item content so as to 
    adjust the padding correspondingly
   */
  @property({ type: Boolean, reflect: true })
  iconOnly: boolean | undefined;

  /**
   * renders segmented control item as loader
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * specifies width of the loader
   */
  @property({ type: String })
  loaderWidth: string | undefined;

  protected slots = new SlotController(this, null, 'before', 'after');

  /**
   * The event dispatched here 'bl-segment-item-select' is used internally
   */
  private handleSegmentSelected(): void {
    if (!this.isLoading && !this.isActive) {
      this.isActive = true;
      this.dispatch({ eventName: 'bl-segment-item-select', detailObj: {} }) as BlocksSegmentItemSelect;
    }
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('isActive')) {
      this.setAttribute('aria-selected', `${this.isActive}`);
    }
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-segmented-control-item', {
      'cat-is-active': this.isActive,
      'cat-is-loading': this.isLoading,
      'cat-is-iconOnly': this.iconOnly,
      'cat-is-disabled': this.disabled,
      'cat-c-segmented-control-item--has-before': this.slots.hasSlotted('before'),
      'cat-c-segmented-control-item--has-after': this.slots.hasSlotted('after')
    });

    return html`
      <div part="base" class="${componentClassNames}">
        ${this.isLoading
          ? html`<div part="wrapper" class="cat-c-segmented-control-item__loader-wrapper">
              <cat-skeleton
                part="skeleton"
                exportparts=${this.getExportParts('skeleton')}
                width=${ifDefined(this.loaderWidth)}
                size="sm"
              ></cat-skeleton>
            </div>`
          : html` <button
              part="button"
              class="cat-c-segmented-control-item__button"
              type="button"
              @click=${this.handleSegmentSelected}
              aria-pressed=${ifDefined(this.isActive)}
              ?disabled=${this.disabled}
              tabindex="-1"
            >
              <div part="wrapper" class="cat-c-segmented-control-item__wrapper" tabindex=${this.disabled ? '-1' : '0'}>
                <slot part="part-before" name="before" class="cat-c-segmented-before-slot"></slot>
                <slot part="part-default"></slot>
                <slot part="part-after" name="after" class="cat-c-segmented-after-slot"></slot>
              </div>
            </button>`}
      </div>
    `;
  }
}

if (customElements.get('cat-segmented-control-item') === undefined) {
  customElements.define('cat-segmented-control-item', BlocksSegmentedControlItem);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-segmented-control-item': BlocksSegmentedControlItem;
  }
}
